import { ReactNode, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { FaArrowRight } from "react-icons/fa";
import DisplayBoard from "@/chessBoard/components/DisplayBoard";
import { useSettings } from "@/redux/settings";
import useTranslation from "next-translate/useTranslation";

export function LessonProgressBar({
  value,
  label,
}: {
  value: number;
  label: string;
}) {
  const percent = Number.isFinite(value)
    ? Math.max(0, Math.min(100, value))
    : 0;
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent}
      className="h-2 overflow-hidden rounded-full bg-athens-gray-200"
    >
      <div
        className="h-full rounded-full bg-primary transition-all"
        style={{ width: `${percent}%` }}
      />
    </div>
  );
}

export default function LessonCourseCard({
  href,
  title,
  fen,
  white = true,
  description,
  detail,
  progress,
  action,
  recommended = false,
}: {
  href: string;
  title: string;
  fen?: string;
  white?: boolean;
  description?: string;
  detail?: ReactNode;
  progress: { label: string; value: number; detail?: string }[];
  action: string;
  recommended?: boolean;
}) {
  const { t } = useTranslation("common");
  const { chessSet } = useSettings();
  const container = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState(0);
  useEffect(() => {
    const element = container.current;
    if (!element) return;
    const resize = () =>
      setSize(Math.max(1, Math.floor(element.getBoundingClientRect().width)));
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return (
    <Link
      href={href}
      className="flex h-full flex-col gap-4 rounded-xl border border-athens-gray-200 bg-white p-4 transition-colors duration-200 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-altblue-500 md:p-6"
    >
      <div>
        {recommended && (
          <p className="mb-2 text-xs font-semibold text-altblue-600">
            {t("lessonLibrary.recommended")}
          </p>
        )}
        <h2 className="text-lg font-semibold text-slate-900 md:text-xl">
          {title}
        </h2>
      </div>
      <div className="flex flex-1 flex-col items-center gap-4 lg:flex-row lg:gap-6">
        <div
          ref={container}
          className="pointer-events-none aspect-square w-full max-w-[200px] shrink-0 overflow-hidden rounded-md"
        >
          {size > 0 && (
            <DisplayBoard
              fen={fen}
              boardSize={size}
              playingWhite={white}
              chessSet={chessSet}
              ariaLabel={t("lessonLibrary.preview", { title })}
            />
          )}
        </div>
        <div className="flex w-full flex-1 flex-col gap-4">
          {description && (
            <p className="text-sm leading-6 text-slate-600">{description}</p>
          )}
          {detail && <p className="text-xs text-slate-500">{detail}</p>}
          {progress.map((item) => (
            <div key={item.label}>
              <div className="mb-2 flex items-center justify-between gap-2 text-sm text-slate-700">
                <span>{item.label}</span>
                <span className="shrink-0 font-semibold">
                  {item.detail || `${item.value}%`}
                </span>
              </div>
              <LessonProgressBar
                value={item.value}
                label={`${title}: ${item.label}`}
              />
            </div>
          ))}
          <span className="mt-auto flex items-center justify-end gap-2 pt-1 text-sm font-semibold text-slate-800">
            {action}
            <FaArrowRight aria-hidden="true" />
          </span>
        </div>
      </div>
    </Link>
  );
}
